import { useState } from "react";
import type { ReactNode } from "react";
import { CARDS_BY_ID, GROUP_INFO, JAIL_FINE, BOARD } from "@grundbesitz/shared";
import type { PlayerAction, PlayerState, RoomView } from "@grundbesitz/shared";
import { formatMoney, fieldName } from "../../lib/format";
import { ariaKeys, keysFor } from "../../lib/hotkeys";
import {
  availability,
  buyOfferFor,
  currentPlayerOf,
  debtViewFor,
  isSpectating,
  nameOf,
  rentClaimLabel,
  selfPlayerOf,
  statusLine,
} from "../../lib/gameView";
import { GroupSwatch } from "../board/GroupBadge";
import { TokenIcon } from "../board/TokenIcon";
import { Button } from "../ui/Button";
import { Kbd } from "../ui/Kbd";
import { ConfirmDialog } from "./ConfirmDialog";
import { useAct, useCountdown } from "./hooks";
import { EyeIcon } from "./icons";
import { useUiStore } from "./uiStore";

const primaryClass = "min-h-14 flex-1 text-lg sm:min-w-56 sm:flex-none";

/** Buttons that send one game action. Disabled with the engine's reason when the action is not allowed. */
function useActionButtons(room: RoomView) {
  const { run, busy } = useAct();
  const game = room.game!;
  const selfId = room.you.playerId;
  const check = (action: PlayerAction) => availability(game, selfId, action);
  return { run, busy, check };
}

function AutoPlayNote({ room }: { room: RoomView }) {
  const at = room.timers.autoPlayAt;
  const seconds = useCountdown(at?.at ?? null);
  if (!at || seconds === null) return null;
  const name = nameOf(room.game, room.members, at.playerId);
  return (
    <p className="rounded-lg bg-surface-2 px-3 py-1.5 text-sm text-ink-2">
      {name} ist getrennt. In {seconds} s übernimmt der Auto-Modus.
    </p>
  );
}

function BuildWindowNote({ room }: { room: RoomView }) {
  const window = room.game?.buildWindow;
  const seconds = useCountdown(window && !window.closing ? room.timers.buildWindowEndsAt : null);
  if (!window) return null;
  const what = window.kind === "hotel" ? "Hotel" : "Haus";
  return (
    <p className="rounded-lg bg-accent-soft px-3 py-1.5 text-sm text-ink" role="status">
      {window.closing
        ? `Das letzte ${what} wird versteigert.`
        : `Letztes ${what}: Anmeldung läuft noch ${seconds ?? 0} s.`}
    </p>
  );
}

function StatusHeader({ room }: { room: RoomView }) {
  const game = room.game!;
  const status = statusLine(game, room.members, room.you.playerId);
  const waiting = game.phase === "DEBT" || game.phase === "MORTGAGE_DECISION" ? null : currentPlayerOf(game);
  const shown: PlayerState | null = waiting ?? (status.waitingFor[0] ? (selfPlayerOf(game, status.waitingFor[0]) ?? null) : null);
  return (
    <div className="flex items-center gap-2 text-sm">
      {shown && <TokenIcon token={shown.token} color={shown.color} size={22} title={shown.name} />}
      <p className={`font-semibold ${status.yourMove ? "text-ink" : "text-ink-2"}`} aria-live="polite">
        {status.text}
      </p>
    </div>
  );
}

function RentClaimButton({ room }: { room: RoomView }) {
  const { run, busy, check } = useActionButtons(room);
  const label = rentClaimLabel(room.game!, room.members);
  if (!label || room.game!.rentClaim?.owner !== room.you.playerId) return null;
  const claim = check({ type: "CLAIM_RENT" });
  return (
    <div className="rounded-xl border-2 border-accent bg-accent-soft p-3">
      <Button
        variant="primary"
        className="min-h-14 w-full text-base"
        disabled={busy || !claim.ok}
        title={claim.ok ? undefined : claim.reason}
        aria-keyshortcuts={ariaKeys(keysFor({ type: "CLAIM_RENT" }))}
        onClick={() => void run({ type: "CLAIM_RENT" })}
      >
        {label}
        <Kbd keys={keysFor({ type: "CLAIM_RENT" })} />
      </Button>
      <p className="mt-1.5 text-xs text-ink-2">Die Miete verfällt, sobald der Nächste würfelt.</p>
    </div>
  );
}

function DebtBanner({ room }: { room: RoomView }) {
  const game = room.game!;
  const selfId = room.you.playerId!;
  const openTab = useUiStore((s) => s.openTab);
  const { run, busy, check } = useActionButtons(room);
  const [confirming, setConfirming] = useState(false);
  const view = debtViewFor(game, selfId);
  if (!view) return null;
  const first = view.debts[0];
  const creditor = first.creditor === "bank" ? "der Bank" : nameOf(game, room.members, first.creditor);
  const bankruptcy = check({ type: "DECLARE_BANKRUPTCY" });
  const covered = view.maxRaisable >= view.total;
  return (
    <div className="rounded-xl border-2 border-danger bg-surface p-3" role="alert">
      <p className="font-semibold text-danger">
        Du schuldest {creditor} {formatMoney(view.total)}
        {first.field !== null && first.reason === "RENT" ? ` (Miete für ${fieldName(first.field)})` : ""}
      </p>
      <dl className="mt-2 grid grid-cols-3 gap-1.5 text-center">
        <Stat label="Bargeld" value={formatMoney(view.cash)} />
        <Stat label="Es fehlen" value={formatMoney(view.shortfall)} strong />
        <Stat label="Maximal erzielbar" value={formatMoney(view.maxRaisable)} />
      </dl>
      <p className="mt-2 text-sm text-ink-2">
        {covered
          ? "Verkaufe Gebäude, nimm Hypotheken auf oder handle. Sobald das Geld reicht, wird automatisch gezahlt."
          : "Auch mit allen Verkäufen und Hypotheken reicht das nicht. Du kannst nur noch Bankrott erklären."}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant={covered ? "primary" : "secondary"} className="min-h-12 flex-1" onClick={() => openTab("assets")}>
          Besitz verwalten
        </Button>
        {view.hopeless && (
          <Button variant="danger" className="min-h-12 flex-1" disabled={busy || !bankruptcy.ok} onClick={() => setConfirming(true)}>
            Bankrott erklären
          </Button>
        )}
      </div>
      <ConfirmDialog
        open={confirming}
        title="Bankrott erklären?"
        confirmLabel="Bankrott erklären"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          void run({ type: "DECLARE_BANKRUPTCY" });
        }}
      >
        {first.creditor === "bank"
          ? "Du scheidest aus. Deine Gebäude gehen zurück an die Bank, deine Grundstücke werden einzeln versteigert."
          : `Du scheidest aus. Dein Bargeld, deine Grundstücke und Freikarten gehen an ${creditor}.`}
      </ConfirmDialog>
    </div>
  );
}

function Stat({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="rounded-lg bg-surface-2 px-2 py-1.5">
      <dt className="text-xs text-ink-2">{label}</dt>
      <dd className={`tabular-nums ${strong ? "text-lg font-bold text-danger" : "text-base font-semibold"}`}>{value}</dd>
    </div>
  );
}

/** Short explanation under the buttons. Quiet hints are general tips, hidden on phones to save room. */
function Hint({ children, quiet = false }: { children: ReactNode; quiet?: boolean }) {
  return <p className={`text-sm text-ink-2 ${quiet ? "hidden sm:block" : ""}`}>{children}</p>;
}

/** Phase dependent buttons for the player who has to act. */
function MyTurn({ room }: { room: RoomView }) {
  const game = room.game!;
  const selfId = room.you.playerId!;
  const me = selfPlayerOf(game, selfId)!;
  const { run, busy, check } = useActionButtons(room);
  const openTab = useUiStore((s) => s.openTab);
  const setHidden = useUiStore((s) => s.setMortgageDialogHidden);

  const button = (action: PlayerAction, label: ReactNode, opts: { primary?: boolean; className?: string } = {}) => {
    const a = check(action);
    const keys = keysFor(action);
    return (
      <Button
        variant={opts.primary ? "primary" : "secondary"}
        className={opts.primary ? primaryClass : `min-h-12 flex-1 sm:flex-none ${opts.className ?? ""}`}
        disabled={busy || !a.ok}
        title={a.ok ? undefined : a.reason}
        aria-keyshortcuts={ariaKeys(keys)}
        onClick={() => void run(action)}
      >
        {label}
        <Kbd keys={keys} />
      </Button>
    );
  };
  const whyNot = (action: PlayerAction) => {
    const a = check(action);
    return a.ok ? null : <Hint>{a.reason}</Hint>;
  };

  switch (game.phase) {
    case "AWAIT_ROLL":
      return (
        <>
          <div className="flex gap-2">{button({ type: "ROLL_DICE" }, "Würfeln", { primary: true })}</div>
          {game.turn.doublesCount > 0 ? <Hint>Pasch: du würfelst noch einmal.</Hint> : <Hint quiet>Du kannst jederzeit bauen und handeln.</Hint>}
        </>
      );

    case "IN_JAIL_DECISION": {
      const hasCard = me.jailCards.length > 0;
      return (
        <>
          <div className="flex flex-wrap gap-2">
            {button({ type: "PAY_JAIL_FINE" }, `${formatMoney(JAIL_FINE)} zahlen und würfeln`, { primary: true })}
            {hasCard && button({ type: "USE_JAIL_CARD" }, "Freikarte einsetzen")}
            {button({ type: "ROLL_DICE" }, "Pasch versuchen")}
          </div>
          <Hint>
            Bei einem Pasch kommst du frei und ziehst mit diesem Wurf, danach ist der Zug vorbei. Nach dem dritten Fehlversuch zahlst du {formatMoney(JAIL_FINE)}.
          </Hint>
          {whyNot({ type: "PAY_JAIL_FINE" })}
        </>
      );
    }

    case "BUY_DECISION": {
      const offer = buyOfferFor(game, selfId);
      if (!offer) return null;
      const f = BOARD[offer.field];
      return (
        <>
          <div className="flex items-center gap-2 rounded-lg bg-surface-2 px-3 py-2">
            {f.kind === "STREET" && <GroupSwatch group={f.group} size={20} />}
            <p className="text-sm">
              <span className="font-semibold" data-testid="buy-field">
                {offer.fieldName}
              </span>
              {f.kind === "STREET" && <span className="text-ink-2"> · {GROUP_INFO[f.group].name}</span>}
            </p>
            <p className="ml-auto text-sm tabular-nums text-ink-2">Bargeld {formatMoney(offer.cash)}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {button({ type: "BUY_PROPERTY" }, `Kaufen für ${formatMoney(offer.price)}`, { primary: true })}
            {button({ type: "DECLINE_PURCHASE" }, "Versteigern", { className: "min-h-14 text-base" })}
          </div>
          {offer.missing > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <Hint>
                {offer.canRaise
                  ? `Dir fehlen ${formatMoney(offer.missing)}. Nimm vorher Hypotheken auf oder verkaufe Gebäude.`
                  : `Dir fehlen ${formatMoney(offer.missing)}, und selbst mit Hypotheken und Verkäufen reicht es nicht.`}
              </Hint>
              {offer.canRaise && (
                <Button className="min-h-10" onClick={() => openTab("assets")}>
                  Besitz verwalten
                </Button>
              )}
            </div>
          )}
        </>
      );
    }

    case "CARD_CHOICE": {
      const card = game.pending?.kind === "CARD_CHOICE" ? CARDS_BY_ID[game.pending.cardId] : undefined;
      const pay = card?.effect.type === "CHOICE" ? card.effect.payAmount : 10;
      return (
        <>
          <div className="flex flex-wrap gap-2">
            {button({ type: "CHOOSE_CARD_OPTION", choice: "PAY" }, `${formatMoney(pay)} Strafe zahlen`, { primary: true })}
            {button({ type: "CHOOSE_CARD_OPTION", choice: "DRAW" }, "Ereigniskarte ziehen", { className: "min-h-14 text-base" })}
          </div>
          <Hint quiet>Die Ereigniskarte kann Geld bringen oder kosten.</Hint>
        </>
      );
    }

    case "AWAIT_END_TURN":
      return (
        <>
          <div className="flex gap-2">{button({ type: "END_TURN" }, "Zug beenden", { primary: true })}</div>
          <Hint quiet>Du kannst jetzt noch bauen, Hypotheken verwalten und handeln.</Hint>
        </>
      );

    case "AUCTION":
      return <Hint>Versteigerung läuft. Das Fenster zum Bieten öffnet sich von selbst. Bauen und Handeln ruhen so lange.</Hint>;

    case "MORTGAGE_DECISION":
      return (
        <div className="flex gap-2">
          <Button variant="primary" className={primaryClass} onClick={() => setHidden(false)}>
            Über die Hypothek entscheiden
          </Button>
        </div>
      );

    case "DEBT":
      return <DebtBanner room={room} />;

    case "GAME_OVER":
      return null;
  }
}

/** What the viewer sees while somebody else is on the move. */
function Waiting({ room }: { room: RoomView }) {
  const game = room.game!;
  const status = statusLine(game, room.members, room.you.playerId);
  const names = status.waitingFor.map((id) => nameOf(game, room.members, id));
  if (game.phase === "DEBT" || game.phase === "MORTGAGE_DECISION") {
    return <Hint>Wartet auf {names.join(" und ")}.</Hint>;
  }
  if (game.phase === "AUCTION") {
    return <Hint>Versteigerung läuft. Das Fenster zum Bieten öffnet sich von selbst.</Hint>;
  }
  return <Hint quiet>Du kannst jederzeit bauen und handeln.</Hint>;
}

export function ActionBar({ room }: { room: RoomView }) {
  const game = room.game!;
  const selfId = room.you.playerId;
  const spectating = isSpectating(room);
  const iAct =
    !spectating &&
    ((game.phase === "DEBT" && game.debts.some((d) => d.debtor === selfId)) ||
      (game.phase === "MORTGAGE_DECISION" && game.mortgageDecisions.some((d) => d.playerId === selfId)) ||
      (game.phase !== "DEBT" && game.phase !== "MORTGAGE_DECISION" && game.phase !== "AUCTION" && game.turn.playerId === selfId));

  return (
    <section
      aria-label="Aktionen"
      className="max-h-[55dvh] flex-none space-y-2 overflow-y-auto border-t border-line bg-surface p-3 lg:mx-4 lg:mb-3 lg:max-h-none lg:rounded-2xl lg:border lg:shadow-sm"
    >
      <StatusHeader room={room} />
      <AutoPlayNote room={room} />
      <BuildWindowNote room={room} />
      {!spectating && <RentClaimButton room={room} />}
      {spectating ? (
        <p className="flex items-center gap-2 rounded-lg bg-surface-2 px-3 py-2 text-sm font-medium" role="status">
          <EyeIcon size={18} />
          Du schaust zu.
        </p>
      ) : iAct ? (
        <MyTurn room={room} />
      ) : (
        <Waiting room={room} />
      )}
    </section>
  );
}
